import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { io } from "socket.io-client";
import { jwtDecode } from "jwt-decode";
import { FiSend } from "react-icons/fi";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "http://127.0.0.1:9001";
const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:9001";
const ADMIN_ROLES = ["ADMIN", "AADMIN", "SUPERADMIN"];

const formatTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  let h = date.getHours();
  const m = date.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
};

const formatDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  if (isToday) return formatTime(timestamp);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const authHeaders = (token) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

const MessageTicks = ({ status }) => {
  if (status === "sending") {
    return (
      <svg width="14" height="10" viewBox="0 0 14 10" fill="none" style={{ display: "inline-block", verticalAlign: "middle" }}>
        <path d="M1 5l3.5 3.5L12.5 1" stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (status === "sent") {
    return (
      <svg width="18" height="10" viewBox="0 0 18 10" fill="none" style={{ display: "inline-block", verticalAlign: "middle" }}>
        <path d="M1 5l3.5 3.5L12.5 1" stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 5l3.5 3.5L16.5 1" stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (status === "read") {
    return (
      <svg width="18" height="10" viewBox="0 0 18 10" fill="none" style={{ display: "inline-block", verticalAlign: "middle" }}>
        <path d="M1 5l3.5 3.5L12.5 1" stroke="#53bdeb" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 5l3.5 3.5L16.5 1" stroke="#53bdeb" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return null;
};

const chatUi = {
  panel: {
    position: "fixed",
    bottom: "70px",
    right: "24px",
    width: "380px",
    height: "560px",
    background: "#ffffff",
    borderRadius: "18px",
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.12)",
    zIndex: 99991,
    overflow: "hidden",
    animation: "chatPanelIn 0.25s ease",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e5e7eb",
  },
  header: {
    padding: "14px 16px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#ffffff",
  },
  backBtn: {
    width: "30px",
    height: "30px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    color: "#475569",
    background: "#f1f5f9",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  listHeader: {
    padding: "16px",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
  },
  listItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 16px",
    cursor: "pointer",
    borderBottom: "1px solid #f1f5f9",
    background: "#fff",
    transition: "all 0.15s ease",
  },
  messagesWrap: {
    flex: 1,
    overflowY: "auto",
    padding: "14px 12px",
    background: "#f5f7fa",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  senderBubble: {
    maxWidth: "78%",
    padding: "10px 13px",
    borderRadius: "16px 16px 4px 16px",
    background: "#ffffff",
    color: "#111827",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: 1.5,
  },
  receiverBubble: {
    maxWidth: "78%",
    padding: "10px 13px",
    borderRadius: "16px 16px 16px 4px",
    background: "#D9FDD3",
    color: "#111827",
    border: "1px solid #cceec6",
    boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: 1.5,
  },
  mentionBubble: {
    maxWidth: "78%",
    padding: "10px 13px",
    borderRadius: "16px 16px 4px 16px",
    background: "#fef3c7",
    color: "#111827",
    border: "1px solid #fde68a",
    boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: 1.5,
  },
  inputWrap: {
    padding: "10px 12px",
    borderTop: "1px solid #e5e7eb",
    background: "#ffffff",
    position: "relative",
  },
  inputInner: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "999px",
    padding: "6px 6px 6px 14px",
  },
  input: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: "13px",
    fontWeight: 500,
    background: "transparent",
    color: "#111827",
  },
  sendBtn: {
    background: "#25D366",
    color: "#ffffff",
    border: "none",
    borderRadius: "50%",
    width: "36px",
    height: "36px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 4px 12px rgba(37,211,102,0.25)",
  },
};

const POChatList = ({ chatList, loading, onSelect, unreadMap }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={chatUi.listHeader}>
        <div style={{ fontWeight: 700, fontSize: "16px", color: "#0f172a" }}>💬 Chats</div>
        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
          {chatList.length} conversation{chatList.length !== 1 ? "s" : ""}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", background: "#fff" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "30px", color: "#64748b" }}>
            <div className="spinner-border spinner-border-sm me-2" role="status" />
            Loading chats...
          </div>
        ) : chatList.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "#94a3b8" }}>
            <i className="bi bi-chat-slash" style={{ fontSize: "32px", display: "block", marginBottom: "8px" }} />
            No chats yet
          </div>
        ) : (
          chatList.map((chat) => {
            const unread = unreadMap[chat.groupId] || chat.unreadCount || 0;

            return (
              <div
                key={chat.groupId}
                onClick={() => onSelect(chat)}
                style={chatUi.listItem}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: "12px",
                      background: "#dcfce7",
                      color: "#15803d",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "11px",
                      flexShrink: 0,
                    }}
                  >
                    PO
                  </div>

                  {unread > 0 && (
                    <span
                      style={{
                        position: "absolute",
                        top: "-2px",
                        right: "-2px",
                        background: "#16a34a",
                        color: "#fff",
                        borderRadius: "999px",
                        minWidth: "18px",
                        height: "18px",
                        padding: "0 5px",
                        fontSize: "10px",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px solid #fff",
                      }}
                    >
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                    <span style={{ fontWeight: unread > 0 ? 700 : 600, fontSize: "13px", color: "#0f172a" }}>
                      {chat.groupId}
                    </span>
                    <span style={{ fontSize: "11px", color: "#94a3b8", flexShrink: 0 }}>
                      {formatDate(chat.lastTimestamp)}
                    </span>
                  </div>

                  <div
                    style={{
                      fontSize: "12px",
                      color: unread > 0 ? "#334155" : "#64748b",
                      fontWeight: unread > 0 ? 600 : 400,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {chat.lastMessage || "No messages yet"}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const ChatPanel = ({ chat, onBack, currentUser, socket, token }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [typingUser, setTypingUser] = useState("");
  const [mentionList, setMentionList] = useState([]);
  const [mentionDropdown, setMentionDropdown] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [tickMap, setTickMap] = useState({});
  const messagesEndRef = useRef(null);
  const groupId = chat.groupId;

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${REACT_APP_API_BASE_URL}socket/api/getMessagesBygroupId`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({ groupId }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const msgs = Array.isArray(data) ? data : data?.messages || [];
      setMessages(msgs);

      const newTickMap = {};
      msgs.forEach((msg) => {
        if (msg.senderId === currentUser.userId) {
          const isRead = Array.isArray(msg.readBy) && msg.readBy.some((id) => id !== currentUser.userId);
          newTickMap[msg._id || msg.tempId] = isRead ? "read" : "sent";
        }
      });

      setTickMap((prev) => ({ ...prev, ...newTickMap }));
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error("Fetch messages error:", error.message);
    }
  };

  const markRead = () => {
    fetch(`${REACT_APP_API_BASE_URL}socket/api/markMessagesRead`, {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ groupId, userId: currentUser.userId }),
    }).catch((err) => console.error("markRead err:", err));

    socket?.emit("messagesRead", { groupId, readerId: currentUser.userId });
  };

  const fetchMentionUser = async () => {
    try {
      const response = await fetch(`${REACT_APP_API_BASE_URL}socket/api/fetchMentionUser`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({ senderId: currentUser.userId }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setMentionList(data);
    } catch (error) {
      console.error("Mention user fetch error:", error.message);
    }
  };

  useEffect(() => {
    if (!groupId || !socket) return;

    socket.emit("joinRoom", { groupId });
    fetchMessages();
    markRead();
    fetchMentionUser();

    const handleNewMessage = (msg) => {
      if (msg.groupId === groupId) {
        fetchMessages();
        markRead();
      }
    };

    const handleTyping = ({ userName, groupId: chatId }) => {
      if (groupId === chatId && userName !== currentUser.name) {
        setTypingUser(userName);
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 3000);
      }
    };

    const handleMessagesRead = ({ groupId: readGroupId }) => {
      if (readGroupId === groupId) {
        setTickMap((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((key) => {
            if (updated[key] === "sent") updated[key] = "read";
          });
          return updated;
        });
      }
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("typing", handleTyping);
    socket.on("messagesRead", handleMessagesRead);

    return () => {
      socket.emit("leaveRoom", groupId);
      socket.off("newMessage", handleNewMessage);
      socket.off("typing", handleTyping);
      socket.off("messagesRead", handleMessagesRead);
    };
    // eslint-disable-next-line 
  }, [groupId, socket]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    socket?.emit("typing", { userName: currentUser.name, groupId });

    const mentionMatch = value.match(/@(\w*)$/);
    if (mentionMatch) {
      const query = mentionMatch[1]?.toLowerCase();
      const filtered = mentionList.filter((u) => u.name.toLowerCase().startsWith(query));
      setMentionDropdown(filtered);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleMentionSelect = (name) => {
    setInput(input.replace(/@(\w*)$/, `@${name} `));
    setShowDropdown(false);
  };

  const handleSend = () => {
    if (!input.trim() || !socket) return;

    const tempId = `temp_${Date.now()}`;
    const newMsg = {
      tempId,
      groupId,
      chatId: groupId,
      senderId: currentUser.userId,
      senderType: currentUser.type,
      senderName: currentUser.name,
      message: input,
      timestamp: new Date().toISOString(),
    };

    socket.emit("sendMessage", newMsg);
    setMessages((prev) => [...prev, newMsg]);
    setTickMap((prev) => ({ ...prev, [tempId]: "sending" }));

    setTimeout(() => {
      setTickMap((prev) => ({ ...prev, [tempId]: "sent" }));
    }, 600);

    setInput("");
    scrollToBottom();
  };

  const highlightIfMentioned = (msg) => msg?.includes(`@${currentUser.name}`);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={chatUi.header}>
        <button onClick={onBack} style={chatUi.backBtn}>‹</button>

        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "12px",
            background: "#dcfce7",
            color: "#15803d",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: "11px",
            flexShrink: 0,
          }}
        >
          PO
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>{groupId}</div>
          <div style={{ fontSize: "11px", color: "#64748b" }}>Chat with Admin</div>
        </div>
      </div>

      <div style={chatUi.messagesWrap}>
        {messages.map((msg, i) => {
          const isSender = msg.senderId === currentUser.userId;
          const highlight = highlightIfMentioned(msg.message);
          const msgKey = msg._id || msg.tempId || i;
          const tickStatus = isSender ? tickMap[msgKey] || "sent" : null;

          return (
            <div key={i} style={{ display: "flex", justifyContent: isSender ? "flex-end" : "flex-start" }}>
              <div style={isSender ? (highlight ? chatUi.mentionBubble : chatUi.senderBubble) : chatUi.receiverBubble}>
                <div style={{ fontWeight: 600, fontSize: "11px", marginBottom: "4px", color: "#64748b" }}>
                  {isSender ? "You" : msg.senderName}
                </div>

                <div
                  dangerouslySetInnerHTML={{
                    __html: (msg.message || "").replace(
                      /@(\w+)/g,
                      '<span style="color:#b45309;font-weight:700;">@$1</span>'
                    ),
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "4px",
                    marginTop: "6px",
                    fontSize: "11px",
                  }}
                >
                  <span style={{ color: "#94a3b8" }}>
                    {formatTime(msg.timestamp)}
                  </span>

                  {isSender && (
                    <span style={{ marginLeft: "auto", display: "inline-flex" }}>
                      <MessageTicks status={tickStatus} />
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {isTyping && (
          <div style={{ fontStyle: "italic", fontSize: "12px", color: "#64748b", paddingLeft: "4px" }}>
            {typingUser} is typing...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div style={chatUi.inputWrap}>
        {showDropdown && mentionDropdown.length > 0 && (
          <div
            style={{
              position: "absolute",
              bottom: "60px",
              left: "12px",
              right: "12px",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              maxHeight: "140px",
              overflowY: "auto",
              zIndex: 10,
              boxShadow: "0 10px 20px rgba(15,23,42,0.12)",
            }}
          >
            {mentionDropdown.map((u) => (
              <div
                key={u.userId}
                onClick={() => handleMentionSelect(u.name)}
                style={{
                  padding: "10px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f1f5f9",
                  fontSize: "13px",
                  color: "#0f172a",
                }}
              >
                @{u.name}
              </div>
            ))}
          </div>
        )}

        <div style={chatUi.inputInner}>
          <input
            type="text"
            placeholder="Type a message..."
            value={input}
            onChange={handleInputChange}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            style={chatUi.input}
          />
          <button onClick={handleSend} style={chatUi.sendBtn}>
            <FiSend size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

const ChatIcon = () => {
  const [open, setOpen] = useState(false);
  const [chatList, setChatList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [totalUnread, setTotalUnread] = useState(0);
  const [unreadMap, setUnreadMap] = useState({});
  const socketRef = useRef(null);

  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : {};
  const isAdminUser = decoded?.roles?.some((r) => ADMIN_ROLES.includes(r.roleId));

  const currentUser = {
    userId: decoded?.userId,
    name: decoded?.userName,
    type: decoded?.roles?.[0]?.roleId,
  };

  useEffect(() => {
    socketRef.current = io(SOCKET_URL, { transports: ["websocket"] });
    socketRef.current.on("connect", () => console.log("Chat socket connected"));
    socketRef.current.on("disconnect", () => console.log(" Chat socket disconnected"));

    socketRef.current.on("newMessage", (msg) => {
      if (msg.senderId !== currentUser.userId) {
        setUnreadMap((prev) => ({ ...prev, [msg.groupId]: (prev[msg.groupId] || 0) + 1 }));
        setTotalUnread((prev) => prev + 1);
      }
    });

    return () => socketRef.current?.disconnect();
    // eslint-disable-next-line 
  }, []);

  const fetchChatList = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${REACT_APP_API_BASE_URL}socket/api/getVendorChatList`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({ userId: currentUser.userId, userType: currentUser.type }),
      });
      const data = await res.json();
      const list = data?.data || [];
      setChatList(list);

      const map = {};
      let total = 0;
      list.forEach((c) => {
        map[c.groupId] = c.unreadCount || 0;
        total += c.unreadCount || 0;
      });

      setUnreadMap((prev) => {
        const merged = { ...prev };
        Object.keys(map).forEach((k) => {
          merged[k] = Math.max(prev[k] || 0, map[k]);
        });
        return merged;
      });

      setTotalUnread((prev) => Math.max(prev, total));
    } catch (err) {
      console.error("fetchChatList err:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    fetchChatList();
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedChat(null);
  };

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    const wasUnread = unreadMap[chat.groupId] || 0;
    setUnreadMap((prev) => ({ ...prev, [chat.groupId]: 0 }));
    setTotalUnread((prev) => Math.max(0, prev - wasUnread));
  };

  const handleBack = () => {
    setSelectedChat(null);
    fetchChatList();
  };

  if (isAdminUser) return null;

  return (
    <>
      {open &&
        ReactDOM.createPortal(
          <>
            <style>{`
              @keyframes chatPanelIn {
                from { opacity: 0; transform: translateY(18px) scale(0.98); }
                to { opacity: 1; transform: translateY(0) scale(1); }
              }
            `}</style>

            <div
              onClick={handleClose}
              style={{ position: "fixed", inset: 0, zIndex: 99990, background: "transparent" }}
            />

            <div style={chatUi.panel}>
              {selectedChat ? (
                <ChatPanel
                  chat={selectedChat}
                  onBack={handleBack}
                  currentUser={currentUser}
                  socket={socketRef.current}
                  token={token}
                />
              ) : (
                <POChatList
                  chatList={chatList}
                  loading={loading}
                  onSelect={handleSelectChat}
                  unreadMap={unreadMap}
                />
              )}
            </div>
          </>,
          document.body
        )}

      <li className="nav-item dropdown pe-4" title="Chats">
        <button
          className="nav-link nav-icon border-0 bg-transparent"
          type="button"
          onClick={open ? handleClose : handleOpen}
          aria-label="Chat"
        >
          <i className="bi bi-chat-left-text"></i>
          {totalUnread > 0 && (
            <span className="badge bg-success badge-number">
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
      </li>
    </>
  );
};

export default ChatIcon;